import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, Trash2, ArrowRight, Package, Edit2, Check, X, Tag, Home, Layers, Share2, Download, Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/buildings`;

// أنواع المواد
const MATERIAL_TYPES = [
  { id: 'all', label: 'الكل', shortLabel: 'الكل', icon: Package, color: 'gray' },
  { id: 'unit', label: 'مواد الوحدات', shortLabel: 'وحدات', icon: Home, color: 'blue', description: 'للنماذج والشقق' },
  { id: 'area', label: 'مواد المساحة', shortLabel: 'مساحة', icon: Layers, color: 'purple', description: 'تُحسب بالمتر المربع' },
  { id: 'shared', label: 'مشترك', shortLabel: 'مشترك', icon: Share2, color: 'green', description: 'للاثنين' },
];

export default function Materials() {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [activeTypeTab, setActiveTypeTab] = useState('all');
  const [newMaterial, setNewMaterial] = useState({
    code: '', name: '', category: '', unit: '', unit_price: 0, material_type: 'shared'
  });
  const fileInputRef = useRef(null);

  // دالة تصدير المواد إلى Excel
  const handleExportMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials/export/excel`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `materials_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`تم تصدير ${materials.length} مادة بنجاح`);
    } catch (error) {
      toast.error('خطأ في تصدير المواد');
      console.error('Export error:', error);
    }
  };

  // دالة استيراد المواد من Excel
  const handleImportMaterials = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/materials/import/excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { added_materials, added_categories, skipped_materials } = response.data;
      toast.success(`تم استيراد ${added_materials} مادة جديدة (تم تخطي ${skipped_materials} مادة موجودة)`);
      if (added_categories > 0) {
        toast.info(`تمت إضافة ${added_categories} تصنيف جديد`);
      }
      fetchData();
    } catch (error) {
      toast.error('خطأ في استيراد الملف - تأكد من صحة تنسيق Excel');
      console.error('Import error:', error);
    }
    
    // إعادة تعيين input الملف
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // البحث عن المواد المشابهة
  const similarMaterials = newMaterial.name.length >= 2 
    ? materials.filter(m => 
        m.name.toLowerCase().includes(newMaterial.name.toLowerCase()) ||
        newMaterial.name.toLowerCase().includes(m.name.toLowerCase())
      ).slice(0, 5)
    : [];

  // توليد كود المادة تلقائياً
  const generateMaterialCode = (materialType, currentMaterials = materials) => {
    const prefixes = {
      'unit': 'UNT',    // مواد الوحدات
      'area': 'ARE',    // مواد المساحة
      'shared': 'SHR',  // مشترك
    };
    const prefix = prefixes[materialType] || 'MAT';
    
    // البحث عن أعلى رقم موجود لهذا النوع
    const existingCodes = currentMaterials
      .filter(m => m.code?.startsWith(prefix))
      .map(m => {
        const num = parseInt(m.code.replace(prefix, ''), 10);
        return isNaN(num) ? 0 : num;
      });
    
    const maxNum = existingCodes.length > 0 ? Math.max(...existingCodes) : 0;
    const newNum = String(maxNum + 1).padStart(3, '0');
    return `${prefix}${newNum}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  // توليد الكود الأولي بعد تحميل المواد
  useEffect(() => {
    if (materials.length >= 0 && !newMaterial.code) {
      const initialCode = generateMaterialCode(newMaterial.material_type, materials);
      setNewMaterial(prev => ({ ...prev, code: initialCode }));
    }
  }, [materials]);

  const fetchData = async () => {
    try {
      const [materialsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/materials`),
        axios.get(`${API}/categories`)
      ]);
      setMaterials(materialsRes.data);
      setCategories(categoriesRes.data);
      if (categoriesRes.data.length > 0 && !newMaterial.category) {
        setNewMaterial(prev => ({ ...prev, category: categoriesRes.data[0].name }));
      }
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: newCategoryName.trim() });
      toast.success('تم إضافة التصنيف');
      setNewCategoryName('');
      setShowAddCategory(false);
      fetchData();
    } catch (error) {
      toast.error('خطأ في إضافة التصنيف');
    }
  };

  const handleDeleteCategory = async (id, name) => {
    const inUse = materials.some(m => m.category === name);
    if (inUse) {
      toast.error('لا يمكن حذف تصنيف مستخدم');
      return;
    }
    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('تم حذف التصنيف');
      fetchData();
    } catch (error) {
      toast.error('خطأ في الحذف');
    }
  };

  // تحديث الكود عند تغيير نوع المادة
  const handleMaterialTypeChange = (newType) => {
    const newCode = generateMaterialCode(newType);
    setNewMaterial(prev => ({ ...prev, material_type: newType, code: newCode }));
  };

  const handleAdd = async () => {
    if (!newMaterial.name) {
      toast.error('الرجاء إدخال اسم المادة');
      return;
    }
    // توليد كود إذا لم يكن موجوداً
    const currentCode = newMaterial.code || generateMaterialCode(newMaterial.material_type);
    const materialToAdd = {
      ...newMaterial,
      code: currentCode
    };
    try {
      await axios.post(`${API}/materials`, materialToAdd);
      toast.success('تمت الإضافة بنجاح');
      
      // تحديث المواد أولاً ثم توليد الكود الجديد
      const materialsRes = await axios.get(`${API}/materials`);
      const updatedMaterials = materialsRes.data;
      setMaterials(updatedMaterials);
      
      // توليد كود جديد للمادة التالية باستخدام القائمة المحدثة
      const nextCode = generateMaterialCode(newMaterial.material_type, updatedMaterials);
      setNewMaterial({ 
        code: nextCode, name: '', category: categories[0]?.name || '', unit: '', unit_price: 0, 
        material_type: newMaterial.material_type
      });
    } catch (error) {
      toast.error('خطأ في الإضافة');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد فعلاً حذف هذه المادة من الكتالوج؟')) return;
    try {
      await axios.delete(`${API}/materials/${id}`);
      toast.success('تم الحذف');
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        // بناء رسالة تفصيلية بالمشاريع والأماكن التي تُستخدم فيها المادة
        const parts = [detail.message || 'لا يمكن الحذف'];
        if (Array.isArray(detail.references) && detail.references.length > 0) {
          const summary = detail.references.slice(0, 5).map(r =>
            `${r.project}: ${(r.locations || []).join('، ')}`
          ).join(' | ');
          parts.push(summary);
          if (detail.total_projects > 5) {
            parts.push(`و ${detail.total_projects - 5} مشروع آخر...`);
          }
        }
        toast.error(parts.join(' • '), { duration: 10000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'خطأ في الحذف');
      }
    }
  };

  const handleStartEdit = (mat) => {
    setEditingId(mat.id);
    setEditData({ ...mat, material_type: mat.material_type || 'shared' });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/materials/${editingId}`, editData);
      toast.success('تم التحديث');
      setEditingId(null);
      fetchData();
    } catch (error) {
      toast.error('خطأ في التحديث');
    }
  };

  // Filter materials by type
  const filteredMaterials = materials.filter(mat => {
    if (activeTypeTab === 'all') return true;
    const matType = mat.material_type || 'shared';
    return matType === activeTypeTab;
  });

  // Group by category
  const groupedMaterials = filteredMaterials.reduce((acc, mat) => {
    const cat = mat.category || 'أخرى';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(mat);
    return acc;
  }, {});

  // Count by type
  const typeCounts = {
    all: materials.length,
    unit: materials.filter(m => m.material_type === 'unit').length,
    area: materials.filter(m => m.material_type === 'area').length,
    shared: materials.filter(m => !m.material_type || m.material_type === 'shared').length,
  };

  const getTypeLabel = (type) => {
    const found = MATERIAL_TYPES.find(t => t.id === type);
    return found?.shortLabel || 'مشترك';
  };

  const getTypeColor = (type) => {
    const colors = {
      unit: 'bg-blue-100 text-blue-700',
      area: 'bg-purple-100 text-purple-700',
      shared: 'bg-green-100 text-green-700',
    };
    return colors[type] || colors.shared;
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hidden file input for import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImportMaterials}
        accept=".xlsx,.xls"
        className="hidden"
      />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-600" />
            المواد الأساسية
          </h1>
          <p className="text-sm text-gray-500 mt-1">{materials.length} مادة • {categories.length} تصنيف</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleExportMaterials} variant="outline" size="sm" className="gap-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">تصدير</span>
          </Button>
          <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="gap-1 text-blue-600 border-blue-300 hover:bg-blue-50">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">استيراد</span>
          </Button>
          <Button onClick={() => navigate('/buildings')} variant="outline" size="sm" className="gap-1">
            <ArrowRight className="w-4 h-4" />
            <span className="hidden sm:inline">العودة</span>
          </Button>
        </div>
      </div>

      {/* Material Type Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
        {MATERIAL_TYPES.map((type) => (
          <button
            key={type.id}
            onClick={() => setActiveTypeTab(type.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
              activeTypeTab === type.id
                ? type.id === 'unit' ? 'bg-blue-600 text-white shadow-md'
                : type.id === 'area' ? 'bg-purple-600 text-white shadow-md'
                : type.id === 'shared' ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-700 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <type.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{type.label}</span>
            <span className="sm:hidden">{type.shortLabel}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              activeTypeTab === type.id ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {typeCounts[type.id]}
            </span>
          </button>
        ))}
      </div>

      {/* Categories Section */}
      <Card className="shadow-md border-0 mb-4">
        <CardHeader className="bg-gradient-to-r from-violet-500 to-purple-600 text-white p-3 rounded-t-xl">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="w-5 h-5" />
              التصنيفات
            </CardTitle>
            <Button 
              onClick={() => setShowAddCategory(true)} 
              size="sm" 
              variant="secondary"
              className="h-7 text-xs"
            >
              <Plus className="w-3 h-3 ml-1" />
              تصنيف جديد
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <div 
                key={cat.id} 
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm"
                style={{ backgroundColor: cat.color + '20', color: cat.color, border: `1px solid ${cat.color}40` }}
              >
                <span>{cat.name}</span>
                <button 
                  onClick={() => handleDeleteCategory(cat.id, cat.name)}
                  className="ml-1 hover:opacity-70"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {showAddCategory && (
              <div className="flex items-center gap-2">
                <Input
                  placeholder="اسم التصنيف"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="h-8 w-32"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <Button size="sm" onClick={handleAddCategory} className="h-8 w-8 p-0 bg-green-600">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddCategory(false)} className="h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Form */}
      <Card className="shadow-md border-0 mb-4">
        <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-3 rounded-t-xl">
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-5 h-5" />
            إضافة مادة جديدة
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <div>
              <Label className="text-xs">نوع المادة *</Label>
              <select
                value={newMaterial.material_type}
                onChange={(e) => handleMaterialTypeChange(e.target.value)}
                className="h-9 w-full rounded-md border px-2 text-sm"
              >
                <option value="unit">🏠 مواد وحدات</option>
                <option value="area">📐 مواد مساحة</option>
                <option value="shared">🔄 مشترك</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">الكود (تلقائي)</Label>
              <Input
                value={newMaterial.code}
                onChange={(e) => setNewMaterial({ ...newMaterial, code: e.target.value.toUpperCase() })}
                className="h-9 font-mono bg-gray-50"
                readOnly
              />
            </div>
            <div>
              <Label className="text-xs">الاسم *</Label>
              <Input
                placeholder="مثال: حديد تسليح 12 مم"
                value={newMaterial.name}
                onChange={(e) => setNewMaterial({ ...newMaterial, name: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">التصنيف</Label>
              <select
                value={newMaterial.category}
                onChange={(e) => setNewMaterial({ ...newMaterial, category: e.target.value })}
                className="h-9 w-full rounded-md border px-2 text-sm"
              >
                {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">الوحدة</Label>
              <Input
                placeholder="قطعة"
                value={newMaterial.unit}
                onChange={(e) => setNewMaterial({ ...newMaterial, unit: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">السعر</Label>
              <Input
                type="number"
                placeholder="0"
                value={newMaterial.unit_price}
                onChange={(e) => setNewMaterial({ ...newMaterial, unit_price: parseFloat(e.target.value) || 0 })}
                className="h-9"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleAdd} className="w-full bg-blue-600 hover:bg-blue-700 h-9">
                <Plus className="w-4 h-4 ml-1" />
                إضافة
              </Button>
            </div>
          </div>
          
          {/* عرض المواد المشابهة */}
          {similarMaterials.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-bold text-amber-700 mb-2">⚠️ مواد مشابهة موجودة:</p>
              <div className="flex flex-wrap gap-2">
                {similarMaterials.map(m => (
                  <span 
                    key={m.id} 
                    className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs border border-amber-300"
                  >
                    {m.code} - {m.name} ({m.unit})
                  </span>
                ))}
              </div>
              <p className="text-xs text-amber-600 mt-2">تأكد من عدم تكرار المادة قبل الإضافة</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Materials by Category */}
      {Object.keys(groupedMaterials).length === 0 ? (
        <Card className="shadow-md border-0">
          <CardContent className="p-8 text-center text-gray-400">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>لا توجد مواد في هذا التصنيف</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedMaterials).map(([category, items]) => (
          <Card key={category} className="shadow-md border-0 mb-4">
            <CardHeader className="bg-gray-50 p-3">
              <CardTitle className="text-base">{category} ({items.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-right">الكود</th>
                      <th className="p-2 text-right">الاسم</th>
                      <th className="p-2 text-center">النوع</th>
                      <th className="p-2 text-center">الوحدة</th>
                      <th className="p-2 text-center">السعر</th>
                      <th className="p-2 text-center w-24">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((mat) => (
                      <tr key={mat.id} className="border-b hover:bg-gray-50">
                        {editingId === mat.id ? (
                          <>
                            <td className="p-2">
                              <Input value={editData.code} onChange={(e) => setEditData({...editData, code: e.target.value})} className="h-8 w-20 font-mono" />
                            </td>
                            <td className="p-2">
                              <Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="h-8" />
                            </td>
                            <td className="p-2 text-center">
                              <select
                                value={editData.material_type || 'shared'}
                                onChange={(e) => setEditData({...editData, material_type: e.target.value})}
                                className="h-8 rounded-md border px-1 text-xs"
                              >
                                <option value="unit">وحدات</option>
                                <option value="area">مساحة</option>
                                <option value="shared">مشترك</option>
                              </select>
                            </td>
                            <td className="p-2 text-center">
                              <Input value={editData.unit} onChange={(e) => setEditData({...editData, unit: e.target.value})} className="h-8 w-16" />
                            </td>
                            <td className="p-2 text-center">
                              <Input type="number" value={editData.unit_price} onChange={(e) => setEditData({...editData, unit_price: parseFloat(e.target.value) || 0})} className="h-8 w-20" />
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={handleSaveEdit} className="text-green-600 h-8 w-8 p-0">
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} className="text-gray-500 h-8 w-8 p-0">
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="p-2">
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-mono text-xs">{mat.code}</span>
                            </td>
                            <td className="p-2 font-medium">{mat.name}</td>
                            <td className="p-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(mat.material_type)}`}>
                                {getTypeLabel(mat.material_type)}
                              </span>
                            </td>
                            <td className="p-2 text-center text-gray-500">{mat.unit}</td>
                            <td className="p-2 text-center font-bold text-emerald-600">{mat.unit_price?.toLocaleString()}</td>
                            <td className="p-2 text-center">
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleStartEdit(mat)} className="text-blue-600 h-8 w-8 p-0">
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(mat.id)} className="text-red-500 h-8 w-8 p-0">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
